import { FC, useState } from 'react';
import { app, projects } from '../../../@xmcloud/core/messages/en';
import { useGetProjectList } from '../../services/projects';
import {
	XMCSButton,
	HeaderStack,
	ToggleStack,
	CreateItemPlaceHolder,
} from '../../shared-components';
import { ProjectsTable } from './components/ProjectsTable';
import {
	Skeleton,
	Stack,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Text,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverHeader,
	PopoverBody,
} from '@chakra-ui/react';
import { handlePush } from '../../../@xmcloud/utils/helpers';
import { Footer } from '../../shared-components';
import { useSharedState } from '../../../@xmcloud/hooks';
import { IS_SM_SCREEN } from '../../../@xmcloud/hooks/context/useSharedState';
import { useCreateProjectV3 } from '../create-project/old-components/useCreateProjectV3';
import { NewFeatureAnnouncement } from '..';
import { useGetAllEnvironmentsLimitation } from '../../services/environments';

const { all, my, project: enProject, create: enCreate } = projects;

const { projects: enProjects } = app;

export const ProjectsPage: FC = () => {
	const [selectMyProjects, setSelectMyProjects] = useState(false);
	const { enableCreateProjectV3 } = useCreateProjectV3();

	const { data: projectsData, isLoading: isProjectLoading } =
		useGetProjectList();
	const allProjects = projectsData?.data || [];
	const length = allProjects.length;

	const { state: isSmScreen } = useSharedState(IS_SM_SCREEN, false);
	const envHook = useGetAllEnvironmentsLimitation(true);
	const envLimits = envHook?.data;
	const isEnvLimitsLoading = envHook?.isLoading;
	const prodCount = envLimits?.data?.prodCount ?? 0;
	const prodLimit = envLimits?.data?.prodLimit ?? 0;
	const nonProdCount = envLimits?.data?.nonProdCount ?? 0;
	const nonProdLimit = envLimits?.data?.nonProdLimit ?? 0;
	const [isLimitPopoverOpen, setLimitPopoverOpen] = useState(false);

	const handleCreateClick = () => {
		if (prodCount >= prodLimit && nonProdCount >= nonProdLimit) {
			setLimitPopoverOpen((prev) => !prev);
		} else {
			onHandlePush();
		}
	};

	const onHandlePush = () => {
		handlePush(
			enableCreateProjectV3
				? '/create-project/1'
				: '/create-project/combined/1',
		);
	};

	if (!length && !isProjectLoading)
		return (
			<CreateItemPlaceHolder
				title={enProject.toLocaleLowerCase()}
				onClick={handleCreateClick}
				mt={16}
			/>
		);

	return (
		<Stack>
			<HeaderStack title={isProjectLoading ? '' : enProjects} mb={2}>
				{!isSmScreen && (
					<Popover
						isOpen={isLimitPopoverOpen}
						onClose={() => setLimitPopoverOpen(false)}
						placement="bottom-end"
						gutter={4}
						closeOnBlur
					>
						<Skeleton isLoaded={!isProjectLoading} rounded="lg">
							<PopoverTrigger>
								<XMCSButton
									text={enCreate}
									data-testid="create-project-button"
									onClick={handleCreateClick}
								/>
							</PopoverTrigger>
						</Skeleton>
						<PopoverContent
							w="320px"
							maxH="240px"
							overflowY="auto"
							p={3}
							borderWidth="1px"
							borderStyle="solid"
							borderColor="rgba(0, 0, 0, 0.13)"
							bg="chakra-body-bg"
							borderRadius="md"
							opacity={1}
							boxShadow="md"
							display="flex"
							flexDirection="column"
						>
							<PopoverHeader
								fontWeight="semibold"
								borderBottomWidth={0}
							>
								Environment limit reached
							</PopoverHeader>
							<PopoverBody>
								<Text color="purple.500" fontSize="sm">
									{`${prodCount} / ${prodLimit} prod environments used`}
								</Text>
								<Text color="purple.500" fontSize="sm">
									{`${nonProdCount} / ${nonProdLimit} non-prod environments used`}
								</Text>
								<Text mt={2}>
									You’ve reached the maximum number of
									environments for your current subscription.
									To create more, either upgrade your
									subscription or delete an existing
									environment.
								</Text>
							</PopoverBody>
						</PopoverContent>
					</Popover>
				)}
			</HeaderStack>
			{!isEnvLimitsLoading && (
				<Text color="purple.500" fontSize="sm" mb={4}>
					{`${prodCount}/${prodLimit} prod environments used, ${nonProdCount}/${nonProdLimit} non-prod environments used`}
				</Text>
			)}
			<ToggleStack
				index={selectMyProjects ? 1 : 0}
				onChange={(index) => setSelectMyProjects(index === 1)}
				isLazy
				panels={
					<TabPanels>
						<TabPanel p={0}>
							<ProjectsTable
								selectMyProjects={false}
								isCreateButtonDisabled={false}
								allProjects={allProjects}
								isLoading={isProjectLoading}
							/>
						</TabPanel>
						<TabPanel p={0}>
							<ProjectsTable
								selectMyProjects={true}
								isCreateButtonDisabled={false}
								allProjects={allProjects}
								isLoading={isProjectLoading}
							/>
						</TabPanel>
					</TabPanels>
				}
			>
				<Skeleton
					isLoaded={!isProjectLoading}
					maxW="xs"
					rounded="lg"
					maxH={10}
				>
					<TabList>
						<Tab data-testid="all-projects-button">{all}</Tab>
						<Tab data-testid="my-projects-button">{my}</Tab>
					</TabList>
				</Skeleton>
			</ToggleStack>
			{!!isSmScreen && (
				<Footer>
					<XMCSButton
						text={enCreate}
						isDisabled={false}
						data-testid="create-project-button"
						onClick={() => onHandlePush()}
					/>
				</Footer>
			)}
			<NewFeatureAnnouncement />
		</Stack>
	);
};
